import {
  Badge,
  Button,
  Card,
  Code,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { unwrap } from '../api/client';
import { useOrg } from '../api/org';
import { getAgentSession } from '../api/gen';
import { answerAgent, runAgent, type RunEvent } from '../api/run';
import { useSession } from '../lib/session';

/**
 * One session: its trace, and a box to say something into.
 *
 * The turns arrive as they happen rather than at the end. That is the whole
 * reason the run endpoint is a stream and not an operation — watching the
 * model write a script, seeing what it returned, and reading the answer is the
 * product, and a client that waited for a transcript would show a spinner
 * through the only interesting part.
 */
export function SessionPage() {
  const { id = '' } = useParams();
  const { credential, orgId } = useSession();
  const queries = useQueryClient();
  const headers = useOrg();

  const [message, setMessage] = useState('');
  const [live, setLive] = useState<RunEvent[]>([]);
  const [running, setRunning] = useState(false);
  const [question, setQuestion] = useState<{ requestId: string; text: string } | null>(null);
  const [answer, setAnswer] = useState('');
  const bottom = useRef<HTMLDivElement>(null);

  const session = useQuery({
    queryKey: ['session', id],
    queryFn: () => unwrap(getAgentSession({ headers, path: { id } })),
    // While a run is live the stream is the source of truth; polling behind it
    // would show the same turns twice, half a second apart.
    enabled: !running,
  });

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [live.length]);

  const send = useCallback(async () => {
    if (!message.trim()) return;
    setRunning(true);
    setLive([{ type: 'user', content: message }]);
    const text = message;
    setMessage('');
    try {
      await runAgent({
        sessionId: id,
        message: text,
        credential,
        orgId,
        onEvent: (e) => {
          if (e.type === 'input_request') {
            setQuestion({
              requestId: String(e.args?.requestId ?? ''),
              text: e.content ?? 'The agent is asking something.',
            });
          }
          // response_chunk is the same text the terminal `response` carries,
          // arriving piecemeal; rendering both would duplicate the answer.
          if (e.type !== 'response_chunk') setLive((prev) => [...prev, e]);
        },
      });
    } finally {
      setRunning(false);
      setQuestion(null);
      // The persisted trace is now the whole story, including this run.
      queries.invalidateQueries({ queryKey: ['session', id] });
      setLive([]);
    }
  }, [credential, id, message, orgId, queries]);

  const reply = useCallback(async () => {
    if (!question) return;
    await answerAgent(id, question.requestId, answer, credential, orgId);
    setQuestion(null);
    setAnswer('');
  }, [answer, credential, id, orgId, question]);

  const turns = live.length > 0 ? live.map(toTurn) : (session.data?.trace ?? []).map(toTurn);

  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Title order={3}>{session.data?.session.title || 'Session'}</Title>
          <Text c="dimmed" size="sm">
            {session.data?.session.model}
          </Text>
        </div>
        {session.data && (
          <Badge variant="light">{running ? 'running' : session.data.session.status}</Badge>
        )}
      </Group>

      <Stack gap="sm">
        {turns.map((t, i) => (
          <Turn key={i} {...t} />
        ))}
        {running && (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="sm" c="dimmed">
              thinking…
            </Text>
          </Group>
        )}
        <div ref={bottom} />
      </Stack>

      {question && (
        <Card withBorder>
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              The agent is asking:
            </Text>
            <Text size="sm">{question.text}</Text>
            <Group>
              <TextInput
                flex={1}
                value={answer}
                onChange={(e) => setAnswer(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void reply();
                }}
              />
              <Button onClick={() => void reply()}>Answer</Button>
            </Group>
          </Stack>
        </Card>
      )}

      <Group align="flex-end">
        <Textarea
          flex={1}
          autosize
          minRows={2}
          placeholder="Say something to the agent"
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
          disabled={running}
        />
        <Button loading={running} disabled={!message.trim()} onClick={() => void send()}>
          Send
        </Button>
      </Group>
    </Stack>
  );
}

interface TurnView {
  kind: string;
  content: string;
}

function toTurn(t: RunEvent | { type: string; content: string }): TurnView {
  return { kind: t.type, content: 'content' in t ? (t.content ?? '') : '' };
}

/**
 * One turn. The JavaScript the model wrote is shown as code and its result
 * beside it, because "what did it actually do" is the question a trace exists
 * to answer.
 */
function Turn({ kind, content }: TurnView) {
  if (!content) return null;
  switch (kind) {
    case 'user':
      return (
        <Paper withBorder p="sm" bg="var(--mantine-color-default-hover)">
          <Text size="sm">{content}</Text>
        </Paper>
      );
    case 'execute_js':
      return (
        <Card withBorder p="sm">
          <Text size="xs" c="dimmed" mb={4}>
            thinking
          </Text>
          <Code block>{content}</Code>
        </Card>
      );
    case 'execute_js_result':
      return (
        <Card withBorder p="sm">
          <Text size="xs" c="dimmed" mb={4}>
            result
          </Text>
          <Code block>{content}</Code>
        </Card>
      );
    case 'response':
      return (
        <Paper withBorder p="sm">
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {content}
          </Text>
        </Paper>
      );
    case 'error':
      return (
        <Paper withBorder p="sm" c="red">
          <Text size="sm">{content}</Text>
        </Paper>
      );
    default:
      return null;
  }
}
